import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { templateService } from '../services/db';
import App from '../AppExports';
import PageTitle from '../components/PageTitle';
import './EditorPage.css';
import { captureFullPageScreenshot } from '../utils/thumbnailGenerator';

console.log("EditorPage is importing App:", typeof App);

// Save notification component
const SaveNotification = ({ isVisible, progress, status, message }) => {
  if (!isVisible) return null;
  
  return (
    <div className={`save-notification ${status}`}>
      <div className="save-notification-content">
        {status === 'saving' && (
          <>
            <div className="save-spinner"></div>
            <span>Saving template...</span>
            {progress < 100 && (
              <div className="save-progress-container">
                <div className="save-progress-bar" style={{ width: `${progress}%` }}></div>
              </div>
            )}
          </>
        )}
        
        {status === 'success' && (
          <>
            <svg className="check-icon" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
            <span>{message || 'Draft saved successfully'}</span>
          </>
        )}
        
        {status === 'error' && (
          <>
            <svg className="error-icon" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <span>Error saving template</span>
          </>
        )}
      </div>
    </div>
  );
};

function EditorPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { currentUser } = useAuth();
  const [templateName, setTemplateName] = useState('');
  const [elements, setElements] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ 
    visible: false, 
    progress: 0, 
    status: 'idle',
    message: '' 
  });
  const [isPublishedTemplate, setIsPublishedTemplate] = useState(false);
  const [isAdminTemplate, setIsAdminTemplate] = useState(false);
  const [originalTemplateId, setOriginalTemplateId] = useState(null);
  
  // Load template if editing an existing one
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setIsLoaded(false);
        if (templateId) {
          console.log("Loading template with ID:", templateId);
          const template = await templateService.getById(templateId);
          
          if (template) {
            console.log("Template loaded:", template);
            setTemplateName(template.name);
            setSavedTemplateId(template.id);
            setCurrentThumbnail(template.thumbnail || null); // Load existing thumbnail
            setIsPublishedTemplate(template.isPublished || false);
            setIsAdminTemplate(template.isAdminTemplate || false);
            setOriginalTemplateId(templateId); // Store original ID in case we create a copy
            
            if (template.elements && Array.isArray(template.elements)) {
              setElements(template.elements);
            }
          } else {
            console.error("Template not found");
            alert("Template not found");
            navigate('/');
          }
        }
      } catch (err) {
        console.error("Error loading template:", err);
      } finally {
        setIsLoaded(true);
      }
    };
    
    loadTemplate();
  }, [templateId, navigate]);
  
  // Use useCallback to prevent recreating the setElements function on each render
  const handleElementsChange = useCallback((newElements) => {
    if (process.env.NODE_ENV === 'development' && !window.__LAST_EDITOR_LOG__ || 
        Date.now() - window.__LAST_EDITOR_LOG__ > 5000) { // Only log every 5 seconds max
      console.log(`Editor elements updated: ${newElements.length} items`);
      window.__LAST_EDITOR_LOG__ = Date.now();
    }
    setElements(newElements);
  }, []);
  
  // Add thumbnail handler
  const handleThumbnailGenerated = useCallback((thumbnail) => {
    console.log("🖼️ [EditorPage] Thumbnail generated callback triggered:", {
      hasThumbnail: !!thumbnail,
      thumbnailLength: thumbnail?.length,
      currentThumbnailExists: !!currentThumbnail
    });
    
    if (thumbnail) {
      // Verify thumbnail is valid
      if (typeof thumbnail !== 'string' || !thumbnail.startsWith('data:image/')) {
        console.error("❌ [EditorPage] Invalid thumbnail format received:", 
          thumbnail ? thumbnail.substring(0, 30) + '...' : 'null');
        return;
      }
      
      console.log("✅ [EditorPage] Setting valid thumbnail with length:", thumbnail.length);
      setCurrentThumbnail(thumbnail);
    } else if (thumbnail === null && currentThumbnail) {
      console.log("ℹ️ [EditorPage] Thumbnail explicitly set to null, clearing current thumbnail");
      setCurrentThumbnail(null);
    } else {
      console.log("ℹ️ [EditorPage] No new thumbnail generated, keeping current:", 
        currentThumbnail ? "has existing thumbnail" : "no existing thumbnail");
    }
  }, [currentThumbnail]);
  
  // Handle saving template
  const handleSaveTemplate = async () => {
    try {
      // Show saving notification
      setSaveStatus({ visible: true, progress: 10, status: 'saving', message: '' });
      
      // Check if name is empty
      if (!templateName.trim()) {
        alert("Please enter a template name");
        setSaveStatus({ visible: false, progress: 0, status: 'idle', message: '' });
        return;
      }
      
      // Enable preview mode for thumbnail generation
      setIsPreviewMode(true);
      
      // Update progress
      setSaveStatus({ visible: true, progress: 25, status: 'saving', message: '' });
      
      // Prepare the template data
      const templateData = {
        name: templateName.trim(),
        elements: elements,
        userId: currentUser.id,
        updatedAt: new Date().toISOString()
      };
      
      // Update progress
      setSaveStatus({ visible: true, progress: 50, status: 'saving', message: '' });
      
      // Capture screenshot for thumbnail
      console.log("📥 [EditorPage] Attempting to capture canvas screenshot for template");
      let screenshot = null;
      
      try {
        // Try to capture the screenshot
        screenshot = await captureFullPageScreenshot();
        
        if (screenshot) {
          console.log("✅ [EditorPage] Canvas screenshot captured successfully, length:", screenshot.length);
          templateData.thumbnail = screenshot;
        } else {
          console.error("❌ [EditorPage] Failed to capture canvas screenshot");
        }
      } catch (screenshotError) {
        console.error("❌ [EditorPage] Error capturing canvas screenshot:", screenshotError);
      }

      // If no screenshot was captured, use existing thumbnail
      if (!screenshot && currentThumbnail) {
        console.log("🔄 [EditorPage] Using already generated thumbnail instead of screenshot");
        templateData.thumbnail = currentThumbnail;
      } else if (!screenshot && savedTemplateId) {
        try {
          console.log("🔍 [EditorPage] Checking if existing template has a thumbnail...");
          const existingTemplate = await templateService.getById(savedTemplateId);
          if (existingTemplate && existingTemplate.thumbnail) {
            console.log("✅ [EditorPage] Preserving existing thumbnail from previous version");
            templateData.thumbnail = existingTemplate.thumbnail;
            templateData.thumbnailPath = existingTemplate.thumbnailPath;
          }
        } catch (existingError) {
          console.error("❌ [EditorPage] Error fetching existing template:", existingError);
        }
      }
      
      // Update progress
      setSaveStatus({ visible: true, progress: 75, status: 'saving', message: '' });
      
      // Create or update template based on whether we have a saved ID
      console.log("💾 [EditorPage] Saving template to database");
      let result;
      
      if (savedTemplateId) {
        console.log("🔄 [EditorPage] Updating existing template:", savedTemplateId);
        result = await templateService.update(savedTemplateId, templateData);
        
        // Check if we got back a copied template instead of an update
        if (result.wasCopied) {
          console.log("📋 [EditorPage] Template was copied instead of updated:", result.id);
          // Update the savedTemplateId to the new copy's ID
          setSavedTemplateId(result.id);
          // Update the original ID to new ID
          setOriginalTemplateId(result.id);
          // Show success message indicating a copy was created
          setSaveStatus({ 
            visible: true, 
            progress: 100, 
            status: 'success', 
            message: 'Created your own copy of this template' 
          });
          
          // Update URL to reflect the new template ID (without page reload)
          window.history.replaceState(
            null, 
            document.title, 
            `/templates/${result.id}/edit`
          );
        } else {
          console.log("✅ [EditorPage] Template updated successfully:", result);
          setSaveStatus({ visible: true, progress: 100, status: 'success', message: '' });
        }
      } else {
        console.log("➕ [EditorPage] Creating new template");
        result = await templateService.create(templateData);
        setSavedTemplateId(result.id);
        // Update the original ID to new ID
        setOriginalTemplateId(result.id);
        console.log("✅ [EditorPage] New template created successfully:", result);
        
        // Update URL to reflect the new template ID (without page reload)
        window.history.replaceState(
          null, 
          document.title, 
          `/templates/${result.id}/edit`
        );
        
        setSaveStatus({ visible: true, progress: 100, status: 'success', message: '' });
      }
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      // Hide the success notification after a delay
      setTimeout(() => {
        setSaveStatus({ visible: false, progress: 0, status: 'idle', message: '' });
      }, 3000);
      
      return result;
    } catch (error) {
      console.error("❌ [EditorPage] Error saving template:", error);
      
      // Show error notification
      setSaveStatus({ visible: true, progress: 100, status: 'error', message: '' });
      
      // Disable preview mode
      setIsPreviewMode(false);
      
      // Hide the error notification after a delay
      setTimeout(() => {
        setSaveStatus({ visible: false, progress: 0, status: 'idle', message: '' });
      }, 3000);
      
      return null;
    }
  };
  
  return (
    <div className="editor-page">
      <PageTitle title={templateName || "Template Editor"} />
      <div className="editor-header">
        <div className="editor-controls">
          <button className="back-btn" onClick={() => navigate('/templates')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 8H1M1 8L8 1M1 8L8 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <input
            type="text"
            className="template-name-input"
            placeholder="Untitled Template"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
          {isPublishedTemplate && !currentUser.isAdmin && (
            <div className="published-template-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z" fill="currentColor"/>
              </svg>
              <span>Published Template - Editing will create your own copy</span>
            </div>
          )}
        </div>
        <button className="save-template-btn" onClick={handleSaveTemplate} disabled={saveStatus.status === 'saving'}>
          {saveStatus.status === 'saving' ? 'Saving...' : (isPublishedTemplate && !currentUser.isAdmin ? 'Save as My Copy' : 'Save Template')}
        </button>
      </div>
      
      <SaveNotification 
        isVisible={saveStatus.visible}
        progress={saveStatus.progress}
        status={saveStatus.status}
        message={saveStatus.message}
      />
      
      <div className="editor-container">
        {isLoaded && (
          <App 
            key={templateId || 'new-template'}
            initialElements={elements}
            onElementsChange={{
              call: handleElementsChange,
              onThumbnailGenerated: handleThumbnailGenerated
            }}
            isPreviewMode={isPreviewMode}
          />
        )}
      </div>
    </div>
  );
}

export default EditorPage; 